import * as React from "react";
import './newsletter.css';
import {LoaderSpinner} from "./LoaderSpinner";
import {Contact} from "../model/Contact";
import {Account} from "../model/Account";
import {CheckBox} from "./CheckBox";
import {ErlButtonBig} from "./ErlButtonBig";
import {Option} from "./Select";
import {InputFieldState} from "../../shared/helpers/InputFieldState";

interface NewsletterState {
    loading: boolean,
    error: string,
    saveSuccess: boolean,
    contact: Contact,
    account: Account,
    unsubscribe: string,
    newsletter: number[],
    magazine: InputFieldState,
    erl: InputFieldState,
    partner: InputFieldState,
}

interface NewsletterStateProps {
    id: string,
    hash: string,
    type: number,
    unsubscribe: number,
}

enum Type {
    contact = 1,
    account = 2
}

enum NewsletterType {
    request = 1,
    event = 2,
    info = 3,
    magazine = 4,
    erl = 5,
    account = 100000000,
    kyc = 100000001,
    orderInvestment = 100000002,
    wallet = 100000003,
    taxDocument = 100000005,
    marketplace = 100000006,
}

enum NewsletterTypeString {
    'Neuigkeiten zu Mein ERL' = 1,
    'Erl Magazin' = 4,
    'Erl Newsletter' = 5,
    'Partner Newsletter' = 999
}

/**
 * Newsletter
 */
export class Newsletter extends React.Component<NewsletterStateProps, NewsletterState> {

    state: NewsletterState = {
        loading: true,
        error: '',
        unsubscribe: NewsletterTypeString[this.props.unsubscribe],
        saveSuccess: false,
        contact: null,
        account: null,
        newsletter: [],
        magazine: {value: ''},
        erl: {value: ''},
        partner: {value: ''},
    }

    apiHeaders: any = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }

    componentDidMount() {
        if (this.props.type === Type.contact) {
            // get contact
            this.getContact();
        } else if (this.props.type === Type.account) {
            // get account
            this.getAccount();
        }
    }

    /**
     * get contact
     */
    getContact() {
        this._beforeApiCall();

        fetch('/wp-json/erl-api/v1/newsletter/contact/get?id=' + this.props.id + '&key=' + this.props.hash, {
            method: 'GET',
            headers: this.apiHeaders
        })
            .then(res => res.json())
            .then((data) => {
                if (data.success) {
                    // set contact
                    this.setState({contact: data.contact, loading: false});
                    // set newsletter state
                    this._initNewsletter(data.contact);
                } else {
                    // error handling
                    this.setState({error: data.error, loading: false})
                }
            }).catch(() => {
            this.setState({loading: false, error: 'Unbekannter Fehler aufgetreten'});
        });
    }

    /**
     * init newsletter values. set the state for the checkboxes
     * @param contact
     * @private
     */
    private _initNewsletter(contact: Contact
    ) {
        if (contact.newsletter) {
            this.setState({newsletter: contact.newsletter});
            this._includes(contact.newsletter, NewsletterType.magazine) && this.setState({
                magazine: {
                    value: 'checked',
                    isChecked: true
                }
            });
            this._includes(contact.newsletter, NewsletterType.erl) && this.setState({
                erl: {
                    value: 'checked',
                    isChecked: true
                }
            });
        }
    }

    /**
     * check newsletter contains match value.
     * @param newsletter
     * @param match
     * @private
     */
    _includes(newsletter
                  :
                  number[], match
                  :
                  number
    ) {
        for (let i = 0; i < newsletter.length; i++) {
            if (newsletter[i] === match) {
                return true;
            }
        }
        return false;
    }

    /**
     * get account
     */
    getAccount() {
        this._beforeApiCall();

        fetch('/wp-json/erl-api/v1/newsletter/account/get?id=' + this.props.id + '&key=' + this.props.hash, {
            method: 'GET',
            headers: this.apiHeaders
        })
            .then(res => res.json())
            .then((data) => {
                if (data.success) {
                    this.setState({
                        account: data.account,
                        loading: false,
                        partner: {
                            value: data.account.hasNewsletter ? 'checked' : '',
                            isChecked: data.account.hasNewsletter
                        }
                    });
                } else {
                    // error handling
                    this.setState({error: data.error, loading: false})
                }
            }).catch(() => {
            this.setState({loading: false, error: 'Unbekannter Fehler aufgetreten'});
        });
    }

    /**
     * do before backend api call. set the states.
     * @private
     */
    private _beforeApiCall() {
        this.setState({loading: true, error: '', saveSuccess: false});
    }

    /**
     * save newsletter abonnements for the contact.
     */
    saveContact() {
        this.setState({unsubscribe: ''});
        this._beforeApiCall();

        const newsletter: number[] = this.state.newsletter;
        this.state.magazine.isChecked && newsletter.push(NewsletterType.magazine);
        this.state.erl.isChecked && newsletter.push(NewsletterType.erl);

        fetch('/wp-json/erl-api/v1/newsletter/contact/save', {
            method: 'POST',
            headers: this.apiHeaders,
            body: JSON.stringify({
                id: this.props.id,
                key: this.props.hash,
                newsletter: newsletter
            })
        })
            .then(res => res.json())
            .then((data) => {
                this._afterSave(data);
            }).catch(() => {
            this.setState({loading: false, error: 'Unbekannter Fehler aufgetreten'});
        });
    }

    /**
     * save newsletter abonnements for the account.
     */
    saveAccount() {
        this.setState({unsubscribe: ''});
        this._beforeApiCall();

        fetch('/wp-json/erl-api/v1/newsletter/account/save', {
            method: 'POST',
            headers: this.apiHeaders,
            body: JSON.stringify({
                id: this.props.id,
                key: this.props.hash,
                hasNewsletter: this.state.partner.isChecked
            })
        })
            .then(res => res.json())
            .then((data) => {
                this._afterSave(data);
            }).catch(() => {
            this.setState({loading: false, error: 'Unbekannter Fehler aufgetreten'});
        });
    }

    /**
     * set state after save action
     * @param data
     * @private
     */
    private _afterSave(data: any) {
        if (data.success) {
            this.setState({loading: false, saveSuccess: true});
        } else {
            // error handling
            this.setState({error: data.error, loading: false})
        }
    }

    private _checkSelected(option: Option) {
        let checked = false;
        for (const newsletterKey of this.state.newsletter) {
            if (newsletterKey === parseInt(option.key, 10)) {
                checked = true;
                break;
            }
        }
        return checked;
    }

    render() {

        return <div>
            {this.state.loading ? (
                <LoaderSpinner/>
            ) : (
                <React.Fragment>
                    {this.state.contact ? (
                        <React.Fragment>
                            Hier können Sie Ihre E-Mail-Abonnements verwalten.
                            <div className={"spacer-20"}/>

                            <div className={"newsletter"}>
                                <h2>Erl Magazin</h2>
                                <CheckBox label={"Magazin"} state={this.state.magazine}
                                          onChange={(value: string, isChecked: boolean) => {
                                              this.setState({magazine: {value: value, isChecked: isChecked}})
                                          }}/>
                            </div>

                            <div className={"spacer-40"}/>

                            <div className={"newsletter"}>
                                <h2>Erl Newsletter</h2>
                                <CheckBox label={"Neues von ERL"} state={this.state.erl}
                                          onChange={(value: string, isChecked: boolean) => {
                                              this.setState({erl: {value: value, isChecked: isChecked}})
                                          }}/>
                            </div>

                            {this.message()}

                            <ErlButtonBig label={"Speichern"} onclick={() => this.saveContact()}
                                          className={"btn btn-custom-submit btn-submit newsletter-button-spacer"}/>
                            <ErlButtonBig label={"Alle abmelden"} onclick={() => {
                                this.setState({
                                    newsletter: [],
                                    magazine: {value: '', isChecked: false},
                                    erl: {value: '', isChecked: false},
                                }, () => this.saveContact());
                            }}/>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {this.state.account && (
                                <React.Fragment>
                                    Hier können Sie Ihre Abonnements verwalten.
                                    <div className={"spacer-20"}/>
                                    <CheckBox label={"Partner Newsletter"} state={this.state.partner}
                                              onChange={(value: string, isChecked: boolean) => {
                                                  this.setState({partner: {value: value, isChecked: isChecked}})
                                              }}/>
                                    {this.message()}
                                    <ErlButtonBig label={"Speichern"} onclick={() => this.saveAccount()}/>
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}
        </div>
    }

    /**
     * message handling components
     */
    message() {
        return <React.Fragment>
            <div className={"spacer-20"}/>
            {this.state.saveSuccess && (
                <div className={"newsletter-success"}>Erfolgreich gespeichert</div>
            )}
            {this.state.unsubscribe && (
                <div className={"newsletter-success"}>"{this.state.unsubscribe}" Erfolgreich
                    abgemeldet</div>
            )}
            {this.state.error && (
                <div className={"newsletter-error"}>{this.state.error}</div>
            )}
            <div className={"spacer-20"}/>
        </React.Fragment>
    }
}

